import { Grid, Container, CircularProgress, Box } from '@mui/material';
import React, { useState, useEffect } from 'react';
import GeocodingMap from '../../components/GeocodingMap';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import { deliverableAddressRepo, shipmentRepo } from '../../repositories';
import { H5, H4 } from '../../components/Heading';
import { ColorBox } from '../../components/Box';
import { Apple, Google } from '../../components/Icons';
import { Search } from '@mui/icons-material';
import Copiable from '../../components/Copiable';
import { useRecoilValue } from 'recoil';
import { incidentDetailState } from '../../states/incidents';
import { selectedShipmentIdState } from '../../states/shipment';
import AxlModalConfirm from '../../components/AxlModalConfirm';

export default function AddressEditorContainer({ orig, onClose, onSave, onOverride, classes }) {
  const [address, setAddress] = useState(orig);
  const [unserviceable, setUnserviceable] = useState(false);
  const [confirmUpdateAddress, setConfirmUpdateAddress] = useState(false);
  const [latLng, setLatLng] = useState();
  const [goodToGo, setGoodToGo] = useState();
  const [geocoded, setGeocoded] = useState();
  const [zoom, setZoom] = useState();
  const [validation, setValidation] = useState(undefined);
  const [flagged, setFlagged] = useState([]);
  const [fullAddress, setFullAddress] = useState();
  const [loading, setLoading] = useState(false);
  const selectedIncident = useRecoilValue(incidentDetailState);
  const shipmentId = useRecoilValue(selectedShipmentIdState);

  useEffect(() => {
    setAddress(orig);
    setLatLng(orig?.lat ? { lat: orig.lat, lng: orig.lng } : null);
    setValidation(null);
  }, [orig]);

  const isDiff = (str, orig) => {
    if (str === null || str === undefined) {
      //we will let empty strings through
      return false;
    }
    return (str && !orig) || (orig && !str) || (str && orig && str.trim().toUpperCase() !== orig.trim().toUpperCase());
  };

  const isDiffZip = (addressComponents, orig) => {
    if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
      return false;
    }

    if (!!addressComponents?.componentNotes?.zip) {
      return true;
    }

    if (orig.includes('-') && !!addressComponents?.zip4) {
      return true;
    }
    return false;
  };

  const handleSave = () => {
    if (unserviceable) {
      setConfirmUpdateAddress(true);
    } else {
      onSave?.(address);
    }
  };

  const getDiffZip = (addressComponents, orig) => {
    if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
      return null;
    }

    let zip4 = '';
    if (orig.includes('-') && !!addressComponents?.zip4) {
      zip4 = `-${addressComponents?.zip4}`;
    }
    return `${addressComponents.zip}${zip4}`;
  };

  const validate = async () => {
    setLoading(true);
    setUnserviceable(false);
    try {
      let params = { ...address, street1: address?.street, force_true: 'true' };

      const response = await deliverableAddressRepo.get(params);
      if (response.status === 200) {
        const geocode = response.data.geocode;
        setLatLng({ lat: geocode.latitude, lng: geocode.longitude });
        setGeocoded({ location: geocode });

        let isCorrected = false;
        let updatedAddress = { ...address, lat: geocode.latitude, lng: geocode.longitude };
        let corrections = [];
        if (response?.data?.addressComponents?.isCorrected === true) {
          const addressComponents = response.data?.addressComponents;
          const full = address ? `${addressComponents?.streetLine}, ${addressComponents?.city}, ${addressComponents?.state} ${addressComponents?.zip}`.replaceAll('#', ' ') : '';
          setFullAddress(full);

          if (isDiff(addressComponents?.streetLine, updatedAddress.street)) {
            updatedAddress.street = addressComponents?.streetLine;
            corrections.push('corrected street to ' + addressComponents?.streetLine);
          }

          if (isDiff(addressComponents?.secondaryAddressLine, updatedAddress.street2)) {
            updatedAddress.street2 = addressComponents?.secondaryAddressLine;
            corrections.push('corrected street2 to ' + addressComponents?.secondaryAddressLine);
          }
          if (!!addressComponents?.componentNotes?.city) {
            updatedAddress.city = addressComponents?.city;
            corrections.push('corrected city to ' + addressComponents?.city);
          }

          if (!!addressComponents?.componentNotes?.state) {
            updatedAddress.state = addressComponents?.state;
            corrections.push('corrected state to ' + addressComponents?.state);
          }
          if (isDiffZip(addressComponents, updatedAddress.zipcode)) {
            const correctedZip = getDiffZip(addressComponents, updatedAddress.zipcode);
            updatedAddress.zipcode = correctedZip;
            corrections.push('corrected zipcode to ' + correctedZip);
          }

          setAddress(updatedAddress);
          isCorrected = true;
        }

        const dasResponse = {
          status: 'success',
          isCorrected: isCorrected,
          corrections: corrections,
        };

        if (shipmentId) {
          const serviceable = await shipmentRepo.serviceabilityCheck(shipmentId, updatedAddress);
          if (!serviceable) {
            dasResponse.status = 'unserviceable';
            dasResponse.message = 'The address is in unserviceable area';
            setUnserviceable(true);
          }
        }
        setValidation(dasResponse);
        setAddress(updatedAddress);
        setLoading(false);
        if (response.data?.flaggedIssues && response.data?.flaggedIssues.length > 0) {
          setFlagged(response.data.flaggedIssues);
        } else {
          setFlagged([]);
        }
      } else {
        setLatLng(null);
        const dasResponse = {
          status: 'failed',
          message: response.data?.message,
        };
        setValidation(dasResponse);
        setFlagged([]);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setValidation({ status: 'failed', message: err?.message || 'Validation error' });
      setFlagged([]);
      setUnserviceable(false);
    }
  };

  useEffect(() => {
    setGoodToGo(geocoded?.location && geocoded.location.latitude);
  }, [geocoded]);

  const updateAddress = (datatype) => (event) => {
    let data = {};
    data[datatype] = event.target.value;
    setAddress((a) => ({ ...a, ...data }));
    setGeocoded(null);
    setValidation(null);
  };

  useEffect(() => {
    const full = address ? `${address.street}, ${address.city}, ${address.state} ${address.zipcode}`.replaceAll('#', ' ') : '';
    setFullAddress(full);
  }, [address]);

  const addressEditor = () => {
    return (
      <>
        <AxlModalConfirm
          isOpen={confirmUpdateAddress}
          handleClose={() => {
            setConfirmUpdateAddress(false);
            setUnserviceable(false);
          }}
          componentChildren={<Box textAlign={'center'}>The updated address is in unserviceable area. Do you want to process?</Box>}
          textOK={'Continue'}
          handleOK={() => {
            onSave?.(address);
            setConfirmUpdateAddress(false);
            setUnserviceable(false);
          }}
          maxWidth={'xs'}
        />
        <Grid container>
          <Grid size={12}>
            <H4>Street</H4>
            <OutlinedInput onChange={updateAddress('street')} fullWidth style={{ height: '40px' }} value={address ? address.street : ''} />
          </Grid>
          <Grid size={12}>
            <H4>Street2</H4>
            <OutlinedInput onChange={updateAddress('street2')} fullWidth style={{ height: '40px' }} value={address ? address.street2 : ''} />
          </Grid>
          <Grid size={12}>
            <H4>City</H4>
            <OutlinedInput onChange={updateAddress('city')} fullWidth style={{ height: '40px' }} value={address ? address.city : ''} />
          </Grid>
          <Grid size={12} container spacing={1}>
            <Grid size={5}>
              <H4>State</H4>{' '}
            </Grid>
            <Grid size={7}>
              <H4>Zipcode</H4>{' '}
            </Grid>
            <Grid size={5}>
              <OutlinedInput onChange={updateAddress('state')} fullWidth style={{ height: '40px' }} value={address ? address.state : ''} />
            </Grid>
            <Grid size={7}>
              <OutlinedInput onChange={updateAddress('zipcode')} fullWidth style={{ height: '40px' }} value={address ? address.zipcode : ''} />
            </Grid>
          </Grid>
          <Grid size={12}>
            <ColorBox style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>
              <Copiable txt={fullAddress} />
              <span style={{ display: 'inline-block' }}>
                <a style={{ marginLeft: '10px' }} target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${fullAddress}`}>
                  <Google size="sm" />
                </a>
                <a style={{ marginLeft: '10px' }} target="_blank" rel="noreferrer" href={`https://maps.apple.com/?address=${fullAddress}`}>
                  <Apple size="md" />
                </a>
                <a style={{ marginLeft: '10px', color: '#444' }} target="_blank" rel="noreferrer" href={`https://google.com/search?q=${fullAddress}`}>
                  <Search style={{ marginBottom: '-4px' }} fontSize="small" />
                </a>
              </span>
            </ColorBox>
          </Grid>
          <Grid size={12} container>
            <Container style={{ display: 'flex', justifyContent: 'space-between', height: '40px', marginTop: '10px', padding: 0 }}>
              <Button style={{ fontSize: '11px' }} size={'small'} onClick={validate}>
                Validate and Geocode
              </Button>
              <Button style={{ fontSize: '11px' }} disabled={!goodToGo} size={'small'} onClick={() => handleSave()}>
                {!!selectedIncident ? 'Save and Resolve' : 'Save'}
              </Button>
              <Button style={{ fontSize: '11px' }} size={'small'} onClick={() => onClose && onClose()}>
                Close
              </Button>
            </Container>
          </Grid>
          <Grid size={12}>
            {validation && (
              <ColorBox color={validation.status || ''}>
                <H4>Geocode Result</H4>
                <Grid container>
                  <Grid size={3}>
                    <H5>status:</H5>
                  </Grid>
                  <Grid size={9}>
                    {validation.status}
                  </Grid>
                  {validation.status === 'success' ? (
                    <Grid container>
                      <Grid size={3}>
                        <H5>source:</H5>
                      </Grid>
                      <Grid size={9}>
                        {geocoded?.location.source}
                      </Grid>
                      {geocoded.location && (
                        <Grid size={12}>
                          result: {geocoded.location.latitude}, {geocoded.location.longitude}
                        </Grid>
                      )}
                      {validation.isCorrected && (
                        <Grid container style={{ marginTop: '8px', fontSize: '13px', color: 'red' }}>
                          <Grid size={3}>
                            <H5>Corrections:</H5>
                          </Grid>
                          {validation?.corrections.map((correction, i) => {
                            return (
                              <Grid size={12} key={`item-correction-${i}`}>
                                {correction}
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Grid container>
                      <Grid size={3}>
                        <H5>error:</H5>
                      </Grid>
                      <Grid size={12}>
                        {validation.message}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </ColorBox>
            )}
          </Grid>
          <Grid size={12}>
            {flagged && flagged.length > 0 && (
              <ColorBox color={'failed'}>
                <H4>Address was retrieved from cache but it appears to have some validation issues:</H4>
                <Grid container>
                  {flagged.map((flag, i) => {
                    return (
                      <Grid size={12} key={`item-flagged-${i}`}>
                        {flag}
                      </Grid>
                    );
                  })}
                </Grid>
              </ColorBox>
            )}
          </Grid>
          <Grid size={12}>
            {validation?.status && validation.status !== 'success' && (
              <ColorBox color={validation.status || ''}>
                <Grid container>
                  <Grid size={8}>
                    {validation.status !== 'success' && (
                      <Button style={{ background: 'red' }} onClick={() => onOverride && onOverride(address)}>
                        Click to OVERRIDE Address
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </ColorBox>
            )}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid size={5} style={{ minHeight: '540px' }}>
        {loading ? <CircularProgress color="primary" size={20} /> : addressEditor()}
      </Grid>
      <Grid size={7}>
        <GeocodingMap center={latLng} initialZoom={zoom} google_key={`${import.meta.env.REACT_APP_GOOGLE_KEY}`} markers={latLng ? [{ latLng }] : []} />
      </Grid>
    </Grid>
  );
}
